import { ToastProvider } from "@/components/ui/toast"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Amplify } from "aws-amplify"
import { getCurrentUser } from "aws-amplify/auth"
import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector/cjs"
import Backend from "i18next-http-backend"
import { useEffect, useState } from "react"
import { initReactI18next } from "react-i18next"
import { RouterProvider } from "react-router-dom"
import amplifyconfiguration from "./amplifyconfiguration.json"
import appsyncConfig from "./appsync-config.json"
import { CustomSignIn } from "./components/auth/CustomSignIn"
import { router } from "./routes/routes"
import { BookingSyncProvider } from "./providers/BookingSyncProvider"

// Configuration complète d'Amplify (Auth + AppSync)
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: amplifyconfiguration.aws_user_pools_id,
      userPoolClientId: amplifyconfiguration.aws_user_pools_web_client_id,
      identityPoolId: amplifyconfiguration.aws_cognito_identity_pool_id,
      loginWith: {
        oauth: {
          domain: amplifyconfiguration.oauth.domain,
          scopes: amplifyconfiguration.oauth.scope,
          redirectSignIn: amplifyconfiguration.oauth.redirectSignIn.split(','),
          redirectSignOut: amplifyconfiguration.oauth.redirectSignOut.split(','),
          responseType: amplifyconfiguration.oauth.responseType as "code" | "token",
        },
      },
    },
  },
  API: {
    GraphQL: {
      endpoint: appsyncConfig.aws_appsync_graphqlEndpoint,
      region: appsyncConfig.aws_appsync_region,
      defaultAuthMode: 'userPool',
    },
  },
  Storage: {
    S3: {
      bucket: amplifyconfiguration.aws_user_files_s3_bucket,
      region: amplifyconfiguration.aws_user_files_s3_bucket_region,
    },
  },
})

i18n.use(Backend).use(LanguageDetector).use(initReactI18next).init({
  fallbackLng: "fr",
  debug: true,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
})

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Vérifier l'état d'authentification au chargement
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        await getCurrentUser()
        setIsAuthenticated(true)
      } catch {
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuthState()
  }, [])

  const handleSignIn = () => {
    setIsAuthenticated(true)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600">
          chargement...
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        {isAuthenticated ? (
          <BookingSyncProvider>
            <RouterProvider router={router} />
          </BookingSyncProvider>
        ) : (
          <CustomSignIn onSignIn={handleSignIn} />
        )}
      </ToastProvider>
    </QueryClientProvider>
  )
}

export default App
